/**
 * App Badge Service
 *
 * Manages the numeric badge on the app icon:
 * - PWA/Web: uses navigator.setAppBadge (Chrome/Edge, Safari iOS 16.4+ PWA)
 * - Native iOS/Android (Capacitor): uses @capawesome/capacitor-badge for live badge
 */

class AppBadgeService {
  /** True when running inside Capacitor native app (iOS/Android). */
  private async isNativePlatform(): Promise<boolean> {
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  }

  /**
   * Check if badge is supported: Web Badge API (PWA) or Capacitor native.
   */
  isSupported(): boolean {
    if (typeof navigator === 'undefined') return false;
    const webBadge = 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    const native = cap?.isNativePlatform?.() === true;
    return webBadge || native;
  }

  /**
   * Set a numeric badge on the app icon (0 clears it).
   * Uses native Capacitor Badge plugin when in native app; otherwise Web Badge API.
   */
  async setBadge(count: number): Promise<void> {
    try {
      if (await this.isNativePlatform()) {
        const { Badge } = await import('@capawesome/capacitor-badge');
        if (count === 0) {
          await Badge.clear();
          console.log('✅ App badge cleared (native)');
        } else {
          await Badge.set({ count });
          console.log(`✅ App badge set to ${count} (native)`);
        }
        return;
      }
    } catch (e) {
      console.warn('Native badge not available, falling back to web:', e);
    }

    if (!('setAppBadge' in navigator)) {
      console.log('App Badge API not supported in this browser');
      return;
    }
    try {
      if (count === 0) {
        await this.clearBadge();
      } else {
        await (navigator as unknown as { setAppBadge: (n: number) => Promise<void> }).setAppBadge(count);
        console.log(`✅ App badge set to ${count}`);
      }
    } catch (error) {
      console.error('Error setting app badge:', error);
    }
  }

  /**
   * Clear the badge from the app icon.
   */
  async clearBadge(): Promise<void> {
    try {
      if (await this.isNativePlatform()) {
        const { Badge } = await import('@capawesome/capacitor-badge');
        await Badge.clear();
        console.log('✅ App badge cleared (native)');
        return;
      }
    } catch {
      // fall through to web
    }
    if (!('clearAppBadge' in navigator)) return;
    try {
      await (navigator as unknown as { clearAppBadge: () => Promise<void> }).clearAppBadge();
      console.log('✅ App badge cleared');
    } catch (error) {
      console.error('Error clearing app badge:', error);
    }
  }

  /**
   * Increment the current badge count
   * Note: This requires tracking the count separately since there's no API to read current badge
   */
  async incrementBadge(currentCount: number): Promise<number> {
    const newCount = currentCount + 1;
    await this.setBadge(newCount);
    return newCount;
  }

  /**
   * Decrement the current badge count
   */
  async decrementBadge(currentCount: number): Promise<number> {
    const newCount = Math.max(0, currentCount - 1);
    await this.setBadge(newCount);
    return newCount;
  }

  /**
   * Check if the app is running as an installed PWA
   * Badge API works best when app is installed
   */
  isPWA(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           // @ts-ignore
           window.navigator.standalone === true;
  }

  /**
   * Get platform info for logging/debugging
   */
  getPlatformInfo(): {
    isSupported: boolean;
    isPWA: boolean;
    userAgent: string;
  } {
    return {
      isSupported: this.isSupported(),
      isPWA: this.isPWA(),
      userAgent: navigator.userAgent
    };
  }

  /**
   * Initialize periodic badge updates in service worker (for background updates)
   * This allows badge to update even when app is fully closed
   */
  async initializeBackgroundUpdates(businessId: string, userId: string, accessToken: string): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Send business and user info to service worker for periodic updates
      registration.active?.postMessage({
        type: 'INIT_BADGE_UPDATES',
        business_id: businessId,
        user_id: userId,
        access_token: accessToken
      });

      console.log('📛 Initialized background badge updates for:', { businessId, userId, hasToken: !!accessToken });
    } catch (error) {
      console.error('Error initializing background badge updates:', error);
    }
  }

  /**
   * Manually trigger a badge update from service worker
   */
  async triggerBackgroundUpdate(businessId: string, userId: string, accessToken: string): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      registration.active?.postMessage({
        type: 'UPDATE_BADGE',
        business_id: businessId,
        user_id: userId,
        access_token: accessToken
      });

      console.log('📛 Triggered badge update from service worker');
    } catch (error) {
      console.error('Error triggering badge update:', error);
    }
  }
}

export const appBadgeService = new AppBadgeService();

